<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const i18nRuntime = inject('i18nRuntime')
if (!i18nRuntime) throw new Error('SiteLanguageSwitcher: i18nRuntime provider is required')
const api = inject('api')
if (!api) throw new Error('SiteLanguageSwitcher: api provider is required')

const props = defineProps({
  mobileList: { type: Boolean, default: false },
  mobileSheet: { type: Boolean, default: false },
})

const { locale, t } = useI18n()
const root = ref(null)
const open = ref(false)
// 每种语言用它自己的写法显示，不随当前界面翻译：看不懂当前界面的人也能认出自己的语言
const options = i18nRuntime.locales.map(item => ({ value: item.code, label: item.nativeLabel }))
const currentOption = computed(() => options.find(item => item.value === locale.value))

onMounted(() => {
  document.addEventListener('pointerdown', closeFromOutside)
  document.addEventListener('keydown', closeFromEscape)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', closeFromOutside)
  document.removeEventListener('keydown', closeFromEscape)
})

function closeFromOutside(event) {
  if (event.target.closest('.site-language-sheet')) return
  if (props.mobileSheet && open.value && event.target.closest('.site-language-sheet-backdrop')) return
  if (!root.value.contains(event.target)) open.value = false
}

function closeMobileSheet(event) {
  event.preventDefault()
  event.stopPropagation()
  open.value = false
}

function closeFromEscape(event) {
  if (event.key === 'Escape') open.value = false
}

// 鼠标移到选项上就开始下载该语言包，点下去时通常已经就绪；预取失败不处理，点选时会重新加载并报错
function prefetchLocale(value) {
  i18nRuntime.loadLocale(value).catch(() => {})
}

async function changeLocale(value) {
  open.value = false
  await i18nRuntime.setLocale(value)
  if (!api.tokenStore.getAccessToken()) return
  try {
    await api.account.updateProfile({ locale: value })
  } catch (error) {
    console.error('[visual-game] account locale sync failed', error)
  }
}
</script>

<template>
  <div ref="root" class="site-language-switcher" :class="{ 'has-mobile-list': mobileList, 'has-mobile-sheet': mobileSheet }">
    <button
      class="site-language-trigger"
      type="button"
      :aria-label="t('common.language.label')"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click="open = !open"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c2.25 2.46 3.4 5.46 3.4 9S14.25 18.54 12 21c-2.25-2.46-3.4-5.46-3.4-9S9.75 5.46 12 3Z" />
      </svg>
      <span :lang="currentOption.value">{{ currentOption.label }}</span>
      <i aria-hidden="true"></i>
    </button>
    <div v-if="open" class="site-language-menu" role="listbox">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        role="option"
        :aria-selected="option.value === locale"
        @pointerenter="prefetchLocale(option.value)"
        @click="changeLocale(option.value)"
      >
        <span :lang="option.value">{{ option.label }}</span>
        <svg v-if="option.value === locale" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
      </button>
    </div>
    <Teleport v-if="open && props.mobileSheet" to="body">
      <button
        class="site-language-sheet-backdrop"
        type="button"
        :aria-label="t('visualGame.visual.close')"
        @pointerdown.stop
        @pointerup.stop
        @touchstart.stop
        @touchend.stop
        @click="closeMobileSheet"
      ></button>
      <section class="site-language-sheet" :aria-label="t('common.language.label')">
        <strong class="site-language-sheet-title">{{ t('common.language.label') }}</strong>
        <div role="listbox">
          <button
            v-for="option in options"
            :key="option.value"
            type="button"
            role="option"
            :aria-selected="option.value === locale"
            @pointerenter="prefetchLocale(option.value)"
            @click="changeLocale(option.value)"
          >
            <span :lang="option.value">{{ option.label }}</span>
            <svg v-if="option.value === locale" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
          </button>
        </div>
      </section>
    </Teleport>
    <section v-if="mobileList" class="site-language-mobile">
      <strong>{{ t('common.language.label') }}</strong>
      <div role="listbox">
        <button
          v-for="option in options"
          :key="option.value"
          type="button"
          role="option"
          :aria-selected="option.value === locale"
          @pointerenter="prefetchLocale(option.value)"
          @click="changeLocale(option.value)"
        >
          <span :lang="option.value">{{ option.label }}</span>
        </button>
      </div>
    </section>
  </div>
</template>

<style>
.site-language-switcher { position: relative; display: inline-flex; align-items: center; white-space: nowrap; }
.site-language-trigger { display: inline-flex; height: 28px; align-items: center; gap: 5px; padding: 0 8px; border: 0; border-radius: 6px; background: transparent; color: var(--drama-color-text); font-size: 12px; font-weight: var(--drama-font-weight-regular); cursor: pointer; }
.site-language-trigger:hover,
.site-language-trigger:focus-visible,
.site-language-trigger[aria-expanded='true'] { background: var(--drama-color-surface-alt); }
.site-language-trigger > svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 1.6; }
.site-language-trigger i { width: 5px; height: 5px; margin: -3px 1px 0 2px; border-right: 1px solid currentColor; border-bottom: 1px solid currentColor; opacity: .58; transform: rotate(45deg); }
.site-language-menu { position: absolute; z-index: 40; top: calc(100% + 5px); right: 0; display: grid; width: 124px; padding: 4px 0; border: 1px solid var(--drama-color-border); border-radius: 8px; background: var(--drama-color-surface); box-shadow: var(--drama-shadow-lg); }
.site-language-menu button { display: flex; height: 32px; align-items: center; justify-content: space-between; padding: 0 12px; border: 0; background: transparent; color: var(--drama-color-text); font-size: 12px; text-align: left; cursor: pointer; }
.site-language-menu button:hover,
.site-language-menu button:focus-visible,
.site-language-menu button[aria-selected='true'] { background: var(--drama-color-surface-alt); }
.site-language-menu button svg { width: 13px; height: 13px; fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; stroke-width: 2; }
.site-language-mobile { display: none; }
.site-language-sheet-backdrop, .site-language-sheet, .site-language-sheet-title { display: none; }
@media (max-width: 680px) {
  .site-language-switcher.has-mobile-list { display: block; width: 100%; }
  .has-mobile-list .site-language-trigger,
  .has-mobile-list .site-language-menu { display: none; }
  .site-language-mobile { display: grid; gap: 10px; }
  .site-language-mobile > strong { color: var(--drama-color-text-muted); font-size: 11px; font-weight: var(--drama-font-weight-medium); letter-spacing: .04em; }
  .site-language-mobile > div { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; }
  .site-language-mobile button { display: flex; min-width: 0; height: 38px; align-items: center; gap: 7px; padding: 0 10px; border: 1px solid var(--drama-color-border); border-radius: 8px; background: var(--drama-color-surface); color: var(--drama-color-text); font-size: 11px; cursor: pointer; }
  .site-language-mobile button[aria-selected='true'] { border-color: var(--drama-color-primary); background: var(--drama-color-primary); color: var(--drama-color-on-primary); }
  .site-language-mobile button > span:last-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .has-mobile-sheet .site-language-trigger { width: 38px; height: 38px; justify-content: center; padding: 0; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-full); background: color-mix(in srgb, var(--drama-color-surface) 76%, transparent); }
  .has-mobile-sheet .site-language-trigger > span,
  .has-mobile-sheet .site-language-trigger > i { display: none; }
  .has-mobile-sheet .site-language-trigger > svg { width: 17px; height: 17px; }
  .has-mobile-sheet .site-language-menu { display: none; }
  .site-language-sheet-backdrop { position: fixed; z-index: 89; inset: 0; display: block; width: 100%; height: 100%; padding: 0; border: 0; background: var(--drama-color-overlay); backdrop-filter: blur(4px); }
  .site-language-sheet { position: fixed; z-index: 90; right: 0; bottom: 0; left: 0; display: grid; gap: 12px; padding: 20px 16px calc(20px + env(safe-area-inset-bottom)); border-radius: var(--drama-radius-xl) var(--drama-radius-xl) 0 0; background: var(--drama-color-surface); box-shadow: var(--drama-shadow-lg); }
  .site-language-sheet-title { display: block; color: var(--drama-color-text); font-size: 16px; font-weight: var(--drama-font-weight-semibold); }
  .site-language-sheet > div { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .site-language-sheet button { display: flex; height: 44px; align-items: center; justify-content: space-between; padding: 0 12px; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface); color: var(--drama-color-text); font-size: 12px; }
  .site-language-sheet button[aria-selected='true'] { border-color: var(--drama-color-primary); background: var(--drama-color-primary); color: var(--drama-color-on-primary); }
  .site-language-sheet button > svg { width: 13px; height: 13px; fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; stroke-width: 2; }
}
</style>
